import { useCallback, useEffect, useRef, useState } from 'react'

import { useArabicSessionLanguage } from '../global/context/SessionSettingsContext'

interface HorizontalScrollControl<T extends HTMLElement> {
  containerRef: React.RefObject<T>
  scroll: (direction: 'left' | 'right') => void
  stopScrolling: () => void
  showFadeLeft: boolean
  showFadeRight: boolean
  handleScroll: () => void
}

function getNormalizedScrollLeft(element: HTMLElement, isRTL: boolean): number {
  if (!isRTL) {
    return element.scrollLeft
  }

  const scrollLeft = element.scrollLeft
  const scrollWidth = element.scrollWidth
  const clientWidth = element.clientWidth

  if (scrollLeft < 0) {
    return scrollWidth + scrollLeft - clientWidth
  }

  return scrollWidth - scrollLeft - clientWidth
}

export function useHorizontalControlScroll<T extends HTMLElement>(): HorizontalScrollControl<T> {
  const containerRef = useRef<T | null>(null)
  const scrollAnimationRef = useRef<number | null>(null)
  const [showFadeLeft, setShowFadeLeft] = useState(false)
  const [showFadeRight, setShowFadeRight] = useState(false)
  const isArabic = useArabicSessionLanguage()

  const scroll = useCallback((direction: 'left' | 'right') => {
    const speed = 6.5

    const step = () => {
      if (containerRef.current) {
        containerRef.current.scrollLeft += direction === 'left' ? -speed : speed
      }
      scrollAnimationRef.current = requestAnimationFrame(step)
    }

    scrollAnimationRef.current = requestAnimationFrame(step)
  }, [])

  const stopScrolling = useCallback(() => {
    if (scrollAnimationRef.current) {
      cancelAnimationFrame(scrollAnimationRef.current)
      scrollAnimationRef.current = null
    }
  }, [])

  const updateFadeVisibility = useCallback(() => {
    const container = containerRef.current
    const pxAwayFromTheEdge = 20
    if (!container) {
      return
    }

    const scrollLeft = getNormalizedScrollLeft(container, isArabic) // <-- CHANGED
    const { scrollWidth, clientWidth } = container

    if (isArabic) {
      // scroll is vice versa
      setShowFadeLeft(scrollLeft + clientWidth < scrollWidth - pxAwayFromTheEdge)
      setShowFadeRight(scrollLeft > pxAwayFromTheEdge)
    } else {
      setShowFadeLeft(scrollLeft > pxAwayFromTheEdge)
      setShowFadeRight(scrollLeft + clientWidth < scrollWidth - pxAwayFromTheEdge)
    }
  }, [isArabic])

  useEffect(() => {
    updateFadeVisibility()
  }, [updateFadeVisibility])

  const handleScroll = () => updateFadeVisibility()

  return {
    containerRef,
    scroll,
    stopScrolling,
    showFadeLeft,
    showFadeRight,
    handleScroll,
  }
}
